import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { useNavigate } from 'react-router-dom';

type UserRole = 'admin' | 'monteur';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, role?: UserRole) => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
  userRole: UserRole | null;
  monteurId: string | null;
  userName: string | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [monteurId, setMonteurId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const navigate = useNavigate();
  const authStateChangeTimeout = useRef<number | null>(null);

  const clearAuthState = useCallback(async () => {
    setSession(null);
    setUser(null);
    setUserRole(null);
    setMonteurId(null);
    setUserName(null);
    setError(null);
  }, []);

  const fetchUserRole = useCallback(async (userId: string) => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          role,
          monteur_id,
          monteure (
            name
          )
        `)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching user role:', error);
        return;
      }
      
      if (data) {
        setUserRole(data.role as UserRole);
        setMonteurId(data.monteur_id);
        if (data.monteure?.name) {
          setUserName(data.monteure.name);
        }
      } else {
        setUserRole('admin');
      }
    } catch (error) {
      console.error('Error in fetchUserRole:', error);
    }
  }, []);

  // Initialize auth state with improved error handling and retry logic
  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        if (!mounted) return;

        if (initialSession?.user) {
          setSession(initialSession);
          setUser(initialSession.user);
          await fetchUserRole(initialSession.user.id);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(initializeAuth, 1000 * retryCount);
        } else {
          await clearAuthState();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return;

        // Clear any pending timeouts
        if (authStateChangeTimeout.current) {
          window.clearTimeout(authStateChangeTimeout.current);
        }

        // Set a new timeout for state updates
        authStateChangeTimeout.current = window.setTimeout(async () => {
          try {
            switch (event) {
              case 'SIGNED_IN':
                if (currentSession?.user) {
                  setSession(currentSession);
                  setUser(currentSession.user);
                  await fetchUserRole(currentSession.user.id);
                }
                break;

              case 'SIGNED_OUT':
              case 'USER_DELETED':
                await clearAuthState();
                navigate('/login');
                break;

              case 'TOKEN_REFRESHED':
                if (currentSession?.user) {
                  setSession(currentSession);
                  setUser(currentSession.user);
                } else {
                  await clearAuthState();
                }
                break;

              case 'USER_UPDATED':
                if (currentSession?.user) {
                  setSession(currentSession);
                  setUser(currentSession.user);
                }
                break;
            }
          } catch (error) {
            console.error('Error in auth state change handler:', error);
            await clearAuthState();
          }
        }, 100); // Small delay to batch potential rapid state changes
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (authStateChangeTimeout.current) {
        window.clearTimeout(authStateChangeTimeout.current);
      }
    };
  }, [navigate, clearAuthState, fetchUserRole]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.signInWithPassword({ 
        email, 
        password
      });
      
      if (error) {
        if (error.message === 'Invalid login credentials') {
          setError('Ungültige Anmeldedaten. Bitte überprüfen Sie Ihre E-Mail und Ihr Passwort.');
        } else {
          setError(error.message);
        }
        console.error('Anmeldefehler:', error);
      }
    } catch (error: any) {
      setError(error.message || 'Fehler bei der Anmeldung');
      console.error('Anmeldefehler:', error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, role: UserRole = 'admin') => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            role: role
          }
        }
      });
      
      if (error) {
        setError(error.message);
        console.error('Registrierungsfehler:', error);
      }
    } catch (error: any) {
      setError(error.message || 'Fehler bei der Registrierung');
      console.error('Registrierungsfehler:', error);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        setError(error.message);
        console.error('Abmeldefehler:', error);
      } else {
        await clearAuthState();
      }
    } catch (error: any) {
      setError(error.message || 'Fehler beim Abmelden');
      console.error('Abmeldefehler:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      loading, 
      signIn, 
      signUp, 
      signOut, 
      error, 
      userRole, 
      monteurId,
      userName
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}